// Card bodies on system posts (CHAT.md, AWARDS.md): the week's awards, the
// champion, a team's milestone, a person's achievement (ACHIEVEMENTS.md §12);
// and on a manager's own line, a mock, a player or a game they shared.
// Tapping a team opens their trophy room;
// "All awards" opens the Awards screen at that period. The drawer closes on
// navigation so the destination is visible. A tap anywhere else on a card
// opens its message's reactions, as on any line (CHAT.md: reactions on the
// room's posts too). Drawn through the kit (LOOKS.md
// Part 7); an award's glyph is the look's art.

import { formatInZone, type ChatCard, type TeamRef } from '@fsp/domain';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase, useReaderZone } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { PositionPill, ProTeamCode, TeamMark } from '../kit';
import { TeamFigure } from '../kit/TeamFigure';
import { AchievementGlyph, AwardGlyph, Icon } from '../kit/Icon';
import '../styles/screens/achievements.css';
import { Cup, TIER_LABEL } from '../screens/shared/AwardKit';
import { useChatDrawer } from './chatDrawer';
import { askForCard } from './findAnything';

export function ChatCardBody({ card }: { card: ChatCard }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const real = useLeagueSource() === 'api';
  const drawer = useChatDrawer();
  const zone = useReaderZone();
  const go = (to: string) => {
    drawer.setOpen(false);
    navigate(to);
  };
  const room = (team: TeamRef) => go(`${base}/awards?tab=room&team=${team.id}`);

  switch (card.kind) {
    case 'awards':
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="trophy" size="sm" />
            <span>{card.title}</span>
            <button
              type="button"
              className="fsp-linklike"
              onClick={() =>
                go(
                  card.period === null
                    ? `${base}/awards?tab=season`
                    : `${base}/awards?tab=week&period=${card.period}`,
                )
              }
            >
              All awards
            </button>
          </div>
          {card.items.map((i) => (
            <div key={i.awardId} className="fsp-chat-card-row" data-tone={i.tone}>
              <span className="fsp-award-icon fsp-chat-card-glyph">
                <AwardGlyph icon={i.icon} size={13} />
              </span>
              <div className="fsp-chat-card-body">
                <div className="fsp-chat-card-name">{i.name}</div>
                <button
                  type="button"
                  className="fsp-linklike fsp-chat-card-team"
                  onClick={() => room(i.team)}
                >
                  <TeamMark team={i.team} size={16} />
                  <span>{i.team.name}</span>
                </button>
              </div>
              <span className="fsp-num fsp-chat-card-value">{i.valueLabel}</span>
            </div>
          ))}
        </div>
      );
    case 'champion':
      return (
        <div className="fsp-chat-card">
          <button
            type="button"
            className="fsp-linklike fsp-chat-card-champion fsp-chat-card-full"
            onClick={() => room(card.team)}
          >
            <Cup
              primary={card.team.colors.primary}
              secondary={card.team.colors.secondary}
              size={44}
            />
            <div className="fsp-chat-card-body fsp-chat-card-left">
              <div className="fsp-chat-card-name" data-tone="glory">
                {card.season} {card.trophyName}
              </div>
              <div className="fsp-chat-card-team-name">{card.team.name}</div>
              <div className="fsp-sub fsp-ui-wrap-text">{card.line}</div>
            </div>
          </button>
          {/* THE YEARBOOK (H9): the league's season, told the morning the
              champion is crowned. */}
          <button
            type="button"
            className="fsp-linklike fsp-chat-card-more"
            onClick={() => go(`${base}/yearbook`)}
          >
            <Icon name="yearbook" size="sm" /> Open the yearbook
          </button>
        </div>
      );
    case 'achievement':
      // A PERSON'S FIND, one quiet line (§12). Nothing to open: the badge is
      // theirs, on their account, and a secret is said as one.
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-unlock fsp-chat-card-full fsp-chat-card-found">
            <span className="fsp-ach-chip-glyph" data-shows={card.shows}>
              <AchievementGlyph icon={card.icon} size={16} />
            </span>
            <div className="fsp-chat-card-body fsp-chat-card-left">
              <div className="fsp-chat-card-strong">
                {card.finder} {card.shows === 'open' ? 'earned ' : 'found a secret: '}
                {card.name}
              </div>
              <div className="fsp-sub fsp-ui-wrap-text">{card.line}</div>
            </div>
          </div>
        </div>
      );
    case 'milestone':
      return (
        <div className="fsp-chat-card">
          <button
            type="button"
            className="fsp-linklike fsp-chat-card-unlock fsp-chat-card-full"
            onClick={() => room(card.team)}
          >
            <span className="fsp-badge-glyph fsp-chat-card-badge" data-tier={card.tier}>
              <AwardGlyph icon={card.icon} size={16} />
            </span>
            <div className="fsp-chat-card-body fsp-chat-card-left">
              <div className="fsp-chat-card-strong">
                {card.team.ownerName} unlocked {card.name}
                <span className="fsp-badge-tier fsp-chat-card-tier" data-tier={card.tier}>
                  {TIER_LABEL[card.tier]}
                </span>
              </div>
              <div className="fsp-sub fsp-ui-wrap-text">{card.line}</div>
            </div>
          </button>
        </div>
      );
    case 'draft-recap':
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="recap" size="sm" />
            <span>Draft recap</span>
            <button
              type="button"
              className="fsp-linklike"
              onClick={() => go(`${base}/draft-recap`)}
            >
              Read the recap
            </button>
          </div>
          <div className="fsp-chat-card-recap">
            <div className="fsp-chat-card-headline">{card.headline}</div>
            {card.lines.map((l, i) => (
              <div key={i} className="fsp-chat-card-line">
                {l}
              </div>
            ))}
            {/* THE MORNING AFTER (C11): a real league's draft, played back.
                The practice league drafts in its room and keeps no board. */}
            {real ? (
              <button
                type="button"
                className="fsp-linklike fsp-chat-card-more"
                onClick={() => go(`${base}/draft/replay`)}
              >
                <Icon name="play" size="sm" /> Watch the replay
              </button>
            ) : null}
          </div>
        </div>
      );
    case 'mock-room':
      // A LEAGUE MOCK OPENED for anybody to join (MOCK_DRAFT.md §6.2). The
      // card stays in the chat after the mock is over; the page it opens
      // says whether one is open now.
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="draft" size="sm" />
            <span>Mock with the league</span>
            <button
              type="button"
              className="fsp-linklike"
              onClick={() => go(`${base}/draft/together`)}
            >
              Take a seat
            </button>
          </div>
          <div className="fsp-chat-card-recap">
            <div className="fsp-chat-card-headline">{card.openedBy} opened a mock draft</div>
            <div className="fsp-chat-card-line">
              {card.pickSeconds} sec a pick · bots take the open seats
            </div>
          </div>
        </div>
      );
    case 'hat':
      // THE WEEK'S HAT (PLAN-2026-10-30-the-weekly-hat): its wearers in the
      // hat it went on in, the line that says who picks, and the way to the
      // League page's hat, where the high scorer picks.
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="trophy" size="sm" />
            <span>{`Week ${card.period}’s hat`}</span>
            <button type="button" className="fsp-linklike" onClick={() => go(`${base}/league`)}>
              See the hat
            </button>
          </div>
          <div className="fsp-chat-card-hat">
            <div className="fsp-chat-card-hat-stage">
              {card.wearers.map((t) => (
                <TeamFigure key={t.id} team={t} hat={card.hat} mood="annoyed" height={84} />
              ))}
            </div>
            <div className="fsp-chat-card-line">{card.line}</div>
          </div>
        </div>
      );
    case 'mock':
      // A MANAGER'S OWN MOCK, shared by them (MOCK_DRAFT.md §6.1): what it
      // said when shared, and a door to the mock, which says so if it has
      // gone since.
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="draft" size="sm" />
            {/* Its manager's name is over it, as over their words. */}
            <span>Mock draft</span>
            <button
              type="button"
              className="fsp-linklike"
              onClick={() => go(`${base}/draft/mocks/${card.mockId}`)}
            >
              See the mock
            </button>
          </div>
          <div className="fsp-chat-card-recap">
            <div className="fsp-chat-card-headline">
              Pick {card.slot} of {card.teams} · graded {card.grade}
            </div>
            {card.picks.length > 0 ? (
              <div className="fsp-chat-card-line">First picks: {card.picks.join(', ')}</div>
            ) : null}
          </div>
        </div>
      );
    case 'recap':
      return (
        <div className="fsp-chat-card">
          <div className="fsp-chat-card-head">
            <Icon name="recap" size="sm" />
            <span>Week {card.period} recap</span>
            <button
              type="button"
              className="fsp-linklike"
              onClick={() => go(`${base}/recap?period=${card.period}`)}
            >
              Read the recap
            </button>
          </div>
          <div className="fsp-chat-card-recap">
            <div className="fsp-chat-card-headline">{card.headline}</div>
            <div className="fsp-sub fsp-ui-wrap-text">{card.standfirst}</div>
            {card.lines.map((l, i) => (
              <div key={i} className="fsp-chat-card-line">
                {l}
              </div>
            ))}
          </div>
        </div>
      );
    case 'player':
      // A PLAYER A MANAGER SHARED (PLAN-2026-10-31-players-and-games-in-chat
      // §2): who he is, and his card as it is now on a tap, where his numbers
      // are. The card carries none.
      return (
        <button
          type="button"
          className="fsp-chat-card fsp-chat-share"
          aria-label={`Open ${card.player.name}'s card`}
          onClick={(e) => {
            e.stopPropagation();
            // The drawer goes, as for every door in a card, so the card is seen.
            drawer.setOpen(false);
            askForCard({ id: card.player.id, name: card.player.name });
          }}
        >
          {card.player.positions[0] ? (
            <PositionPill pos={card.player.positions[0]} size="sm" />
          ) : null}
          <span className="fsp-chat-share-name">{card.player.name}</span>
          <ProTeamCode code={card.player.proTeam} />
          <Icon name="next" size="sm" />
        </button>
      );
    case 'matchup':
      // A GAME A MANAGER SHARED (§2): the score as it was when shared, and
      // when; a tap opens the game as it is now.
      return (
        <button
          type="button"
          className="fsp-chat-card fsp-chat-share fsp-chat-share-game"
          aria-label={`Open ${card.label}: ${card.home.name} v ${card.away.name}`}
          onClick={(e) => {
            e.stopPropagation();
            go(`${base}/matchup/${card.matchupId}`);
          }}
        >
          <span className="fsp-chat-share-when">
            <Icon name="matchup" size="sm" />
            <span>{`${card.label} · ${sharedWhen(card.state, card.at, zone)}`}</span>
          </span>
          {[card.home, card.away].map((side) => (
            <span key={side.teamId} className="fsp-chat-share-side">
              <TeamMark team={side} size={20} />
              <span className="fsp-chat-share-name">{side.name}</span>
              {side.score === null ? null : (
                <span className="fsp-num fsp-chat-card-value">{side.score.toFixed(1)}</span>
              )}
            </span>
          ))}
        </button>
      );
  }
}

/**
 * When a shared game stood where it did: *Not started*, *Final*, or *Live at
 * Sun 3:42 PM* in the reader's own zone (B-181).
 */
function sharedWhen(state: 'pre' | 'live' | 'final', at: string, zone: string | null): string {
  if (state === 'pre') return 'Not started';
  if (state === 'final') return 'Final';
  return zone === null
    ? 'Live'
    : `Live at ${formatInZone(at, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`;
}
